/**
 * E002-T016: StreamLain Module
 * E002-T018: Sidebar Panel Registration
 * E012-T015: Cross-Doc Relation Service
 * E012-T018: RBAC Database Visibility Filter
 *
 * Zentraler Einstiegspunkt für alle StreamLain-Features.
 * Registriert Services und Extensions.
 */

import { type Framework } from '@toeverything/infra';

import {
  DefaultServerService,
  ServersService,
  WorkspaceServerService,
} from '../cloud';
import { DocCreateMiddleware, DocScope, DocService, DocsService } from '../doc';
import { AccessibleDocCacheStore } from '../doc/stores/accessible-doc-cache';
import { DocsStore } from '../doc/stores/docs';
import {
  EditorScope,
  EditorService,
  WorkspaceRealtimeService,
} from '../editor';
import { FeatureFlagService } from '../feature-flag';
import { WorkspaceAreaCacheStore } from '../folder-api';
import { OfflineMutationOutboxService } from '../offline-mutation-outbox';
import { AccessibleFolderCacheStore } from '../organize';
import { GuardService } from '../permissions';
import { ExtensionSidebarService, Workbench } from '../workbench';
import { WorkspaceScope, WorkspaceService } from '../workspace';
import {
  configureWorkspaceLocalCacheModule,
  WorkspaceLocalCacheService,
} from '../workspace-local-cache';
import {
  CalendarApiClientService,
  CalendarAppService,
  CalendarLocalProjectionService,
  CalendarMutationOutboxService,
  PlannerPresentationSettingsService,
} from './calendar';
import {
  EdgeSidebarSetup,
  ReactiveEdgeService,
} from './edge-infrastructure/services';
import { MailMutationOutboxService } from './mail/mail-mutation-outbox';
import { PlannerViewPortService } from './planner';
// sl-5k6.5: AI Confirmation Service
import { AIConfirmationService } from './services/ai-confirmation';
import { AutomationService } from './services/automation';
// sl-automation-refactor.4: New Config API Services
import { AutomationAutoSaveService } from './services/automation-auto-save';
import { AutomationConfigApiService } from './services/automation-config-api';
import { AutomationDiscoveryService } from './services/automation-discovery';
// sl-automation-refactor.5: Lifecycle Hooks
import { AutomationLifecycleService } from './services/automation-lifecycle';
import { AutomationSocketService } from './services/automation-socket';
// sl-9qm.4.1: Component Registry
import { ComponentRegistry } from './services/component-registry';
// sl-bpu6.4: Atomic Custom App provisioning (ConfigDB + ContentDB + first row)
import { ConfigProvisionerService } from './services/config-provisioner.service';
import { ContentDbSlugValidator } from './services/content-db-slug-validator';
import { CrossDocAutomationService } from './services/cross-doc-automation';
import { CrossDocRelationService } from './services/cross-doc-relation';
import { DatabaseDiscoveryService } from './services/database-discovery';
import { DocViewStateService } from './services/doc-view-state';
import { ElsterService } from './services/elster';
import { StreamLainEventBus } from './services/event-bus';
import { StreamLainEventRouter } from './services/event-router';
import { ExecutionService } from './services/execution';
import { FileIndexDocCreateMiddleware } from './services/file-index-doc-create-middleware';
import { FullscreenRegistry } from './services/fullscreen-registry';
import { LoggingService } from './services/logging';
import { MarketplaceService } from './services/marketplace';
import { PublishEditorState } from './services/publish-editor-state';
import { RelationIntegrityService } from './services/relation-integrity';
import { ReviewService } from './services/review';
import { RowDetailModalController } from './services/row-detail-modal-controller';
import { StreamLainSidebarSetup } from './services/sidebar-setup';
import { ViewConfigDatabaseService } from './services/view-config-database.service';
// sl-9qm.5.2: Materialized + Prisma Data Sources + Factory
import { ViewDataSourceFactory } from './services/view-datasource-factory';
// sl-trigger-removal.5: TriggerService and TriggerCleanupService removed
import { WebhookService } from './services/webhook';
import { WorkflowService } from './services/workflow';
import { WorkflowClientService } from './services/workflow-client';
import { WorkflowLivePresenceService } from './services/workflow-live-presence';
import { WorkflowLivePresentationService } from './services/workflow-live-presentation';
import { WorkflowTestContextService } from './services/workflow-test-context';
// sl-9qm.5.1: Y.js Data Source
import { YjsViewDataSource } from './services/yjs-view-datasource';
// sl-9qm.3.4: ViewConfig Y.js Storage
import { ViewConfigStore } from './stores/view-config.store';
// sl-728o.1: Tutorial System (sl-u4d2: DemoDocService removed — onboarding is backend-seeded)
import { FirstCreatedDocTelemetryPromptService } from './tutorial/services/first-created-doc-telemetry-prompt';
import { TutorialLauncher } from './tutorial/services/tutorial-launcher';
import { TutorialPersistence } from './tutorial/services/tutorial-persistence';
import { TutorialRegistry } from './tutorial/services/tutorial-registry';
import {
  DatabaseViewLocalDiscoveryService,
  ViewBackedAppApiClientService,
  ViewBackedAppMutationOutboxService,
  ViewBackedAppRegistryService,
  ViewBackedAppService,
} from './view-backed-app';
// sl-9fmv.6.5: Live-apply workflow-patch registry
import {
  configureWorkflowPatchModule,
  PendingPatchRegistry,
} from './workflow-patch';

// Re-export types
export * from './types';

// Re-export services for useService()
export * from './planner';
export { AutomationService } from './services/automation';
// sl-9qm.4.1: Component Registry
export {
  CALENDAR_MAILBOX_LINK_MODES,
  CalendarApiClientService,
  CalendarAppService,
  type CalendarCreateLocalInput,
  type CalendarCreateLocalResult,
  type CalendarEventsResponse,
  CalendarHttpError,
  type CalendarInvitationResponseStatus,
  CalendarLocalProjectionService,
  type CalendarMailboxAreaTransitionAction,
  type CalendarMailboxAreaTransitionPreview,
  type CalendarMailboxAreaTransitionResource,
  type CalendarMailboxAreaTransitionResult,
  type CalendarMailboxLink,
  type CalendarMailboxLinkDecision,
  type CalendarMailboxLinkMailbox,
  type CalendarMailboxLinkMode,
  type CalendarMailboxLinkPatch,
  type CalendarMailboxLinkPolicy,
  type CalendarMailboxLinkReason,
  type CalendarMailboxLinksResponse,
  calendarMailboxLinksUrl,
  type CalendarMailboxLinkUpdateMode,
  type CalendarMailboxLinkVisibility,
  type CalendarMode,
  CalendarMutationOutboxService,
  type CalendarMutationRecord,
  calendarMutationTargetsSource,
  type CalendarProjectedEvent,
  calendarResourceId,
  CalendarServerUnavailableError,
  type CalendarSource,
  type CalendarSourceCandidate,
  type CalendarSourceCandidatesResponse,
  type CalendarSourcesResponse,
  decodeCalendarEventsResponseV2,
  fetchCalendarJson,
  formatCalendarRequestError,
  isCalendarCacheFallbackError,
  isCalendarMutation,
  isCalendarSourceEventEligible,
  PlannerPresentationSettingsService,
  plannerSourceIdentityLabel,
  plannerSourceSelectionKey,
  provisionLocalCalendarDatabase,
} from './calendar';
export {
  VIEW_COMPONENT_TYPES,
  type ViewComponentType,
} from './constants/view-component-types';
export { AutomationDiscoveryService } from './services/automation-discovery';
export { AutomationSocketService } from './services/automation-socket';
export {
  createDefaultFullComplianceState,
  deriveBindingState,
  evaluateGates,
} from './services/compliance-guard';
export { ComponentRegistry } from './services/component-registry';
export {
  CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
  DatabaseViewLocalDiscoveryService,
  ViewBackedAppApiClientService,
  type ViewBackedAppDescriptor,
  type ViewBackedAppKind,
  ViewBackedAppMutationOutboxService,
  ViewBackedAppRegistryService,
  ViewBackedAppService,
  type ViewBackedAppSource,
  type ViewBackedAppSourceCandidate,
  viewBackedAppSourceRefKey,
} from './view-backed-app';
// sl-bpu6.4: Atomic Custom App provisioning (ConfigDB + ContentDB + first row)
export {
  ConfigProvisionerService,
  type ProvisionInput,
  type ProvisionResult,
} from './services/config-provisioner.service';
export {
  ContentDbSlugValidator,
  type SlugValidationResult,
} from './services/content-db-slug-validator';
export {
  CrossDocAutomationService,
  type CrossDocColumnInfo,
  type GetSourceRowsResult,
  type GetTargetColumnsResult,
} from './services/cross-doc-automation';
export {
  type CrossDocRelationResult,
  CrossDocRelationService,
  DatabaseNotFoundError,
  PermissionDeniedError,
} from './services/cross-doc-relation';
export { DatabaseDiscoveryService } from './services/database-discovery';
export {
  StreamLainEventBus,
  type StreamLainEvents,
} from './services/event-bus';
export { StreamLainEventRouter } from './services/event-router';
export { ExecutionService } from './services/execution';
export { LoggingService } from './services/logging';
export { MarketplaceService } from './services/marketplace';
export type {
  Category,
  ClassificationRuleDTO,
  ClassificationSuggestion,
  CompatibilityEvaluation,
  CompatibilityIssue,
  ComplianceState,
  CopilotMigrationResult,
  CopilotMigrationStep,
  DiscoveryResult,
  DiscoveryStatus,
  FullComplianceState,
  GateEvaluation,
  GateResult,
  ImportOptions,
  ImportProgress,
  ImportResult,
  MigrationFeasibility,
  NodeCategory,
  NodeClassificationResult,
  PaginatedResponse,
  WorkflowContext,
  WorkflowEngineNode,
  WorkflowTemplate,
} from './services/marketplace-types';
export type { BindingState } from './services/marketplace-types';
export {
  type DragState,
  PublishEditorState,
} from './services/publish-editor-state';
export { RowDetailModalController } from './services/row-detail-modal-controller';
export { WorkflowClientService } from './services/workflow-client';
export {
  versionPresenceKey,
  WORKFLOW_LIVE_COLLABORATION_FLAG,
  WorkflowLivePresenceService,
  type WorkflowPresenceVersionKey,
} from './services/workflow-live-presence';
export {
  type WorkflowLivePresentationLocalRole,
  WorkflowLivePresentationService,
  workflowPresentationKey,
} from './services/workflow-live-presentation';
export { WorkflowTestContextService } from './services/workflow-test-context';
export type {
  BoundDataRow,
  ViewComponent,
  ViewComponentCategory,
  ViewComponentRenderProps,
  ViewRenderMode,
} from './types/view-component.types';
// sl-9qm.3.4: ViewConfig Y.js Storage
export { RelationIntegrityService } from './services/relation-integrity';
export {
  type ActivityStats,
  type DailyActivity,
  type PendingDecision,
  type PendingDecisionStatus,
  type RelationCandidate,
  ReviewService,
} from './services/review';
export { StreamLainSidebarSetup } from './services/sidebar-setup';
export { ViewConfigStore } from './stores/view-config.store';
export type {
  DataBindingConfig,
  DataFilter,
  DataFilterOp,
  DataSort,
  SeoConfig,
  ViewComponentInstance,
  ViewConfig,
  ViewConfigChangeEvent,
  ViewConfigChangeType,
  ViewConfigLayout,
  ViewConfigSettings,
} from './types/view-config.types';
// sl-trigger-removal.5: TriggerService export removed
export { WebhookService } from './services/webhook';
export { WorkflowService } from './services/workflow';
// sl-automation-refactor.4: New Config API Services
export {
  AutomationAutoSaveService,
  type SaveContext,
  type SaveStatus,
} from './services/automation-auto-save';
export {
  AutomationConfigApiService,
  type ConfigInput,
  type ConfigResponse,
  type ScheduleInput,
  type ScheduleResponse,
} from './services/automation-config-api';
// sl-automation-refactor.5: Lifecycle Hooks
export {
  AutomationLifecycleService,
  type LifecycleEvent,
} from './services/automation-lifecycle';
// sl-5k6.5: AI Confirmation Service
export {
  AIConfirmationService,
  type ConfirmationRequest,
  type ConfirmationResponse,
} from './services/ai-confirmation';

// sl-9qm.4.2: ViewComponent implementations + registration
export { BuilderComponentWrapper } from './components/view-components/builder-wrapper';
export { registerContainerAndContentComponents } from './components/view-components/register-container-content';
// sl-9qm.4.3: Data + Navigation component registration
export { registerDataAndNavComponents } from './components/view-components/register-data-nav';
// sl-9qm.10.3: Widget component registration (Form)
export { registerWidgetComponents } from './components/view-components/register-widget';
export { FormViewComponent } from './components/view-components/widget/form-view';
// sl-9qm.5.1: View Data Source
export { YjsViewDataSource } from './services/yjs-view-datasource';
export type {
  ColumnInfo,
  DatabaseViewData,
  DataSourceConfig,
  IViewDataSource,
  Row,
} from './types/view-datasource.types';
// sl-9qm.5.2: Materialized + Prisma Data Sources + Factory
export {
  type MaterializedSnapshot,
  MaterializedViewDataSource,
} from './services/materialized-view-datasource';
export {
  PrismaViewDataSource,
  type ViewGraphQLClient,
} from './services/prisma-view-datasource';
export {
  type CreateDataSourceOptions,
  type DataSourceType,
  ViewDataSourceFactory,
} from './services/view-datasource-factory';
// sl-8n5m.1.2: legacy DataBindingSection/FieldMapping/schema-validator deleted.
// Filter/Sort bleiben (orthogonal zu capabilities).
// sl-9qm.6.1: ViewRenderer + Error/Unknown Placeholders
export { ComponentErrorPlaceholder } from './components/view-renderer/component-error-placeholder';
export { UnknownComponentPlaceholder } from './components/view-renderer/unknown-component-placeholder';
export { ViewRenderer } from './components/view-renderer/view-renderer';
// sl-9qm.6.2: Responsive Viewport + Drag Reorder
export { ThemeTokenPicker } from './components/view-config-editor/controls/theme-token-picker';
export {
  attachCanvasDragReorder,
  type ReorderCallbacks,
} from './components/view-renderer/canvas-drag-reorder';
export {
  ViewportContainer,
  type ViewportSize,
} from './components/view-renderer/viewport-container';
export { ViewportSwitcher } from './components/view-renderer/viewport-switcher';
// sl-9qm.7: Theming + CSS Variables System
export {
  ThemeProvider,
  type ThemeProviderProps,
} from './components/theme-provider/theme-provider';
export {
  applyThemeToElement,
  generateCustomCssBlock,
  generateFontImportUrl,
  generateThemeVariables,
  normalizeThemeTokens,
} from './services/theme-provider';
export type {
  SpacingDensity,
  ThemeColors,
  ThemeConfig,
  ThemeLayout,
  ThemePreset,
  ThemeToken,
  ThemeTokenCategory,
  ThemeTokenSet,
  ThemeTypography,
} from './types/view-theme.types';
export {
  themeColorsSchema,
  themeConfigSchema,
  themeLayoutSchema,
  themeTokenCategorySchema,
  themeTokenSchema,
  themeTokenSetSchema,
  themeTypographySchema,
} from './types/view-theme.types';

// sl-9qm.9: SEO Configuration
export {
  buildJsonLd,
  type JsonLdContext,
  type JsonLdObject,
} from './services/seo-jsonld-builder';
export {
  buildMetaTags,
  buildMetaTagsFromDerivedSeo,
  buildSearchPreview,
  type MetaTag,
  renderMetaTagsToHtml,
  type SeoTemplateContext,
} from './services/seo-meta-builder';
export {
  escapeHtml,
  escapeJsonLd,
  type EscapeStrategy,
  escapeSvgContent,
  resolveTemplate,
  validateCanonicalUrl,
} from './services/seo-template-engine';
export { seoConfigSchema } from './types/seo-config.schema';

// sl-9qm.12: Row Detail Pages
export { findRelatedItems } from './services/row-related-items';
export {
  buildRowSlugMap,
  generateRowSlug,
  resolveRowSlug,
} from './services/row-slug';
export { extractTocEntries } from './services/row-toc-builder';
export { rowDetailConfigSchema } from './types/row-detail-config.schema';
export type {
  BreadcrumbEntry,
  RelatedItem,
  RowDetailConfig,
  RowNavigation,
  TocEntry,
} from './types/row-detail-config.types';

// sl-9qm.14: Inline Editing
export { useCanEdit } from './hooks/use-can-edit';
export type {
  EditableFieldConfig,
  InlineEditConfig,
  InlineEditType,
  MinEditRole,
} from './types/inline-edit.types';
export {
  DEFAULT_INLINE_EDIT_CONFIG,
  inlineEditConfigSchema,
} from './types/inline-edit.types';
// sl-9qm.14.2 + sl-9qm.14.3: Inline Edit Components + Custom Actions
export type {
  ActionButtonProps,
  DropdownEditorProps,
  DropdownOption,
  EditableCellProps,
  InlineDateEditorProps,
  InlineNumberEditorProps,
  InlineTextEditorProps,
  ReadOnlyCellProps,
} from './components/inline-editors';
export {
  ActionButton,
  DropdownEditor,
  EditableCell,
  InlineDateEditor,
  InlineNumberEditor,
  InlineTextEditor,
  ReadOnlyCell,
} from './components/inline-editors';
// sl-9qm.14.3: Custom Action Types
export type {
  ArchiveAction,
  AutomationAction,
  CustomAction,
  CustomActionType,
  StatusChangeAction,
} from './types/custom-action.types';
export {
  customActionsArraySchema,
  customActionSchema,
} from './types/custom-action.types';

// sl-9qm.10: Public Forms
export type {
  CaptchaProvider,
  FileUploadLimits,
  FormConfig,
  FormConsentConfig,
  FormFieldConfig,
  FormFieldType,
  FormFieldValidation,
  SpamProtectionConfig,
  SubmitAction,
} from './types/form-config.types';
export {
  DEFAULT_FILE_UPLOAD_LIMITS,
  DEFAULT_FORM_CONFIG,
  DEFAULT_FORM_CONSENT,
  DEFAULT_SPAM_PROTECTION,
  fileUploadLimitsSchema,
  formConfigSchema,
  formConsentConfigSchema,
  formFieldConfigSchema,
  spamProtectionConfigSchema,
  submitActionSchema,
} from './types/form-config.types';

// sl-9qm.19: Internal View Overlay
export { InternalViewOverlay } from './components/internal-view';

// sl-9qm.11: View-Scoped Search
export { SearchBar } from './components/search-bar';
export type {
  SearchConfig,
  SearchResponse,
  SearchResult,
} from './types/search.types';
export {
  DEFAULT_SEARCH_CONFIG,
  searchConfigSchema,
} from './types/search.types';

// sl-9qm.22: Doc-Reference Column Type
export { DocReferenceCell } from './components/doc-reference-cell';
export { docReferenceColumnConfigSchema } from './types/doc-reference.schema';
export type {
  DocReferenceColumnConfig,
  DocReferenceRenderMode,
  ResolvedDocReference,
} from './types/doc-reference.types';

// Re-export sidebar components
export { AutomationConfigPanel } from './sidebar';
export { WorkspaceDatabasesPanel } from './sidebar/workspace-databases-panel';
// sl-canvas2-8.31 Phase 6: AIConfirmationDialog REMOVED - use inline Lit confirmation
// See: blocksuite/ai/chat-panel/message/confirmation.ts

// Re-export shared types for E012 coordination
export type { Bound, IEdgeEndpoint } from './shared/edge-endpoint';
export { isEdgeEndpoint } from './shared/edge-endpoint';

// Re-export hooks
// sl-trigger-removal.5: TriggerSyncEventDetail and useTriggerSyncListener removed
export type { AutomationRunEventDetail } from './hooks';
export { useAutomationRuntimeBridge, useExecutionListener } from './hooks';
// sl-n8i1.5: workflow engine UI Gating (sl-y2po.12: hook removed, constants only)
export { WORKFLOW_ENABLED_CLASS } from './hooks';
// sl-y2po.11/.12: Plan-Based Feature Gate
export type { FeatureAvailability, FeatureGateContextValue } from './hooks';
export {
  CollectionsFeatureGuard,
  FEATURE_KEY_COLLECTIONS,
  FEATURE_KEY_ELSTER,
  FEATURE_KEY_TAGS,
  FEATURE_KEY_WORKFLOW,
  FEATURE_KEY_WORKSPACE_MAIL,
  isFeatureAllowedFromBody,
  TagsFeatureGuard,
  useCollectionsFeatureEnabled,
  useFeatureGate,
  useTagsFeatureEnabled,
} from './hooks';

// sl-n8i1.5: Providers
export { WorkflowGateProvider } from './providers';
// sl-y2po.11: Plan-Based Feature Gate Provider
export { FeatureGateProvider } from './providers';
// sl-vw37.1.3: Vertical Gate Provider Factory
export { createVerticalGateProvider } from './providers';

// sl-vw37.1.3: Vertical Definitions
export type { FrontendVerticalDefinition } from './constants/vertical-definitions';
export {
  registerVertical,
  VERTICAL_DEFINITIONS,
} from './constants/vertical-definitions';
// sl-rqa6.6: Feature Locked Inline Banner
export { FeatureLockedBanner } from './components/feature-locked-banner';
// sl-canvas2-8.31 Phase 6: useAIConfirmation REMOVED - use inline Lit confirmation
// See: blocksuite/ai/chat-panel/message/confirmation.ts

// sl-728o: Tutorial System
export type {
  TutorialProgress,
  TutorialSession,
  TutorialState,
  TutorialStep,
  TutorialTrigger,
  TutorialTriggerType,
} from './tutorial';
export {
  TutorialLauncher,
  TutorialPersistence,
  TutorialRegistry,
  TutorialRenderer,
  useTutorialTrigger,
} from './tutorial';

// Re-export 3-Pass Notion Import (E013-S005)
export type {
  Import3PassOptions,
  Import3PassResult,
  ImportStats,
} from './utils/notion-import-3pass';
export { execute3PassImport } from './utils/notion-import-3pass';

// Re-export edge infrastructure (E012)
export {
  createPathStrategy,
  createRoutingStrategy,
  EdgeRenderingService,
  SVGEdgeRenderer,
} from './edge-infrastructure';
export {
  EdgeSidebarSetup,
  ReactiveEdgeService,
} from './edge-infrastructure/services';

// Re-export file import (E024)
export * from './file-import';

// Re-export BlockSuite hooks (sl-fzj)
export {
  getStreamlainHooks,
  registerStreamlainHooks,
  type StreamlainHooks,
} from './blocksuite-hooks';

/**
 * Konfiguriert das StreamLain Module.
 * Muss 1x in modules/index.ts aufgerufen werden.
 */
export function configureStreamLainModule(framework: Framework): void {
  configureWorkspaceLocalCacheModule(framework);
  framework
    .scope(WorkspaceScope)
    .service(AutomationService)
    .service(CrossDocAutomationService, [DocsService, GuardService])
    .service(CrossDocRelationService, [DocsService, GuardService])
    .service(DatabaseDiscoveryService, [
      DocsService,
      GuardService,
      WorkspaceService,
      WorkspaceServerService,
    ])
    .service(AutomationDiscoveryService, [DocsService, GuardService])
    .service(RelationIntegrityService, [
      DocsService,
      DatabaseDiscoveryService,
      CrossDocRelationService,
    ])
    .service(LoggingService)
    .service(ExecutionService, [LoggingService])
    // sl-trigger-removal.5: TriggerService and TriggerCleanupService removed
    // sl-n8i1: WorkflowClientService requires WorkspaceService for workspace-scoped credentials
    .service(WorkflowClientService, [WorkspaceService])
    .service(WorkflowService, [WorkflowClientService])
    .service(WorkflowLivePresenceService, [
      WorkspaceRealtimeService,
      FeatureFlagService,
    ])
    .service(WorkflowLivePresentationService, [
      WorkspaceRealtimeService,
      FeatureFlagService,
      WorkflowLivePresenceService,
    ])
    .service(MarketplaceService, [WorkflowClientService])
    .service(WebhookService)
    .service(ReviewService) // S020-8.20.5
    // sl-automation-refactor.4: Config API Services
    .service(AutomationConfigApiService)
    .service(WorkflowTestContextService, [AutomationConfigApiService])
    .service(AutomationAutoSaveService, [AutomationConfigApiService])
    // sl-automation-refactor.5: Lifecycle Hooks
    .service(AutomationLifecycleService, [
      WorkspaceService,
      AutomationDiscoveryService,
      AutomationConfigApiService,
    ])
    .service(AutomationSocketService, [
      WorkspaceService,
      WorkspaceServerService,
      LoggingService,
      ExecutionService,
      DocsService,
      PendingPatchRegistry, // sl-9fmv.6.5
    ])
    // sl-5k6.5: AI Confirmation Service
    .service(AIConfirmationService, [WorkspaceService])
    .impl(
      DocCreateMiddleware('streamlain-file-index'),
      FileIndexDocCreateMiddleware,
      [DocsStore]
    )
    .service(ElsterService)
    .service(MailMutationOutboxService, [
      WorkspaceServerService,
      OfflineMutationOutboxService,
    ])
    .service(CalendarApiClientService, [WorkspaceServerService])
    .service(ViewBackedAppApiClientService, [WorkspaceServerService])
    .service(ViewBackedAppRegistryService)
    .service(DatabaseViewLocalDiscoveryService, [
      DocsService,
      WorkspaceAreaCacheStore,
      AccessibleFolderCacheStore,
    ])
    .service(CalendarLocalProjectionService, [
      DocsService,
      DatabaseViewLocalDiscoveryService,
      DefaultServerService,
    ])
    .service(ViewBackedAppMutationOutboxService, [
      WorkspaceService,
      ViewBackedAppApiClientService,
      OfflineMutationOutboxService,
      AccessibleDocCacheStore,
      ViewBackedAppRegistryService,
    ])
    .service(ViewBackedAppService, [
      ViewBackedAppApiClientService,
      ViewBackedAppMutationOutboxService,
    ])
    .service(CalendarMutationOutboxService, [
      WorkspaceService,
      CalendarApiClientService,
      OfflineMutationOutboxService,
      ViewBackedAppMutationOutboxService,
    ])
    .service(CalendarAppService, [
      DocsService,
      CalendarApiClientService,
      CalendarLocalProjectionService,
      CalendarMutationOutboxService,
      ViewBackedAppService,
    ])
    .service(PlannerPresentationSettingsService, [
      WorkspaceService,
      WorkspaceServerService,
      DefaultServerService,
      ServersService,
      WorkspaceLocalCacheService,
    ])
    .service(PlannerViewPortService, [DocsService])
    .service(FullscreenRegistry)
    .service(DocViewStateService)
    // sl-9qm.4.1: Component Registry (workspace-wide, shared across docs)
    .service(ComponentRegistry)
    // sl-oll9.15: Typed Event Bus
    .service(StreamLainEventBus)
    .service(StreamLainSidebarSetup, [
      ExtensionSidebarService,
      FullscreenRegistry,
      ComponentRegistry,
    ])
    // sl-oll9.13: Event routing extracted from SidebarSetup
    .service(StreamLainEventRouter, [
      AutomationService,
      Workbench,
      FullscreenRegistry,
    ])
    .service(EdgeSidebarSetup)
    // sl-728o.1: Tutorial System
    .service(TutorialPersistence)
    .service(TutorialRegistry, [TutorialPersistence])
    .service(FirstCreatedDocTelemetryPromptService)
    // sl-728o.7.1: Tutorial Launcher (handles card start events + RBAC)
    .service(TutorialLauncher, [TutorialRegistry]);

  // sl-u4d2: DemoDocService removed — onboarding doc is backend-seeded
  // sl-9qm.3.3: Publish Editor state (selection + drag)
  framework.scope(WorkspaceScope).scope(DocScope).service(PublishEditorState);
  // sl-8n5m.8.3: RowDetail modal controller (per-doc modal state)
  framework
    .scope(WorkspaceScope)
    .scope(DocScope)
    .service(RowDetailModalController);
  // sl-8n5m.8.4: ContentDB Slug-Conflict Validator (stateless, workspace scope)
  framework.scope(WorkspaceScope).service(ContentDbSlugValidator);
  // sl-bpu6.4: ViewConfig DatabaseBlock provisioner (stateless, workspace scope)
  framework.scope(WorkspaceScope).service(ViewConfigDatabaseService);
  // sl-bpu6.4: ConfigProvisioner — atomic ConfigDB + ContentDB + first row
  framework
    .scope(WorkspaceScope)
    .service(ConfigProvisionerService, [
      DocsService,
      ViewConfigDatabaseService,
      WorkspaceServerService,
    ]);
  // sl-9qm.3.4: ViewConfig Y.js Store (collaborative config storage)
  framework
    .scope(WorkspaceScope)
    .scope(DocScope)
    .store(ViewConfigStore, [DocService]);
  // sl-9qm.5.1: Y.js Data Source (builder + internal mode)
  framework.scope(WorkspaceScope).service(YjsViewDataSource, [DocsService]);
  // sl-9qm.5.2: Data Source Factory (selects Yjs/Materialized/Prisma)
  framework
    .scope(WorkspaceScope)
    .service(ViewDataSourceFactory, [YjsViewDataSource]);

  // E018-S009: Reactive edge system in EditorScope
  framework
    .scope(WorkspaceScope)
    .scope(DocScope)
    .scope(EditorScope)
    .service(ReactiveEdgeService, [
      EditorService,
      EdgeSidebarSetup,
      DatabaseDiscoveryService,
      AutomationDiscoveryService,
      DocService,
    ]);

  // sl-9fmv.6.5: PendingPatchRegistry (WorkspaceScope entity)
  configureWorkflowPatchModule(framework);
}
